// WeeklySupervisionList.tsx
import React, { useEffect, useState, useMemo } from "react";
import {
    postSupervisionComment,
    weeklySupervisionService,
} from "../../../services/weeklySupervision/weeklySupervisionService";
import { useAuth } from "../../../firebase/AuthProvider";
import { toast } from "react-toastify";
import { CommentModal } from "./CommentModal";
import Loader from "../loader/Loader";
import { PaginationDto } from "../../../interfaces/Employee/Employee";
import Pagination from "../../ui/Pagination/Pagination";
import moment from "moment";
import { useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faSearch, faUser, faPencil } from "@fortawesome/free-solid-svg-icons";
import { Flag } from "lucide-react";
import "./weeklySupervision.css";
import "../hometabs/Hometabs.css";
import "../taskPage/taskpage.css";

// Simple Edit Icon Component
const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#7e57c2" className="bi bi-pencil-fill" viewBox="0 0 16 16">
        <path d="M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z" />
    </svg>
);

const WeeklySupervisionList: React.FC = () => {
    const { currentUser, currentOrganization } = useAuth();
    const navigate = useNavigate();

    // Data States
    const [data, setData] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState<number>();
    const [supervisionInstance, setSupervisionInstance] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [fetchTrigger, setFetchTrigger] = useState(0);

    // Filtering & Pagination States
    const [pagination, setPagination] = useState<PaginationDto>({ pageNumber: 1, pageSize: 50 });
    const [selectedSubType, setSelectedSubType] = useState("");

    // Search States
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    // Modal & Editing States
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [editingRecordIndex, setEditingRecordIndex] = useState<number | null>(null);
    const [currentComment, setCurrentComment] = useState<string>("");
    const [modalParticipantName, setModalParticipantName] = useState<string>("");
    const [isSavingComment, setIsSavingComment] = useState<boolean>(false);

    // Meeting Info State (Informational Only - No Logic Lock)
    const [meetingDetails, setMeetingDetails] = useState<{ startTime: string } | null>(null);

    // Grouping UI State
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

    // 1. Debounce Effect
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            if (searchTerm !== debouncedSearchTerm) {
                setPagination(prev => ({ ...prev, pageNumber: 1 }));
            }
        }, 500);

        return () => {
            clearTimeout(timerId);
        };
    }, [searchTerm]);

    // 2. Fetch Data Effect
    useEffect(() => {
        const fetchTasks = async () => {
            if (!currentUser) return;
            try {
                const token = await currentUser.getIdToken();
                setLoading(true);

                const result = await weeklySupervisionService(
                    token!,
                    pagination.pageNumber,
                    pagination.pageSize,
                    selectedSubType,
                    debouncedSearchTerm
                );

                setData(result.data.tasks);
                setTotalCount(result.data.count);
                setSupervisionInstance(result.data.supervisionInstance);

                // Handle Meeting Info (Purely for display)
                const instance = result.data.supervisionInstance;
                if (instance?.meetingDetails?.startTime) {
                    setMeetingDetails({ startTime: instance.meetingDetails.startTime });
                } else {
                    setMeetingDetails(null);
                }

            } catch (err) {
                console.error("Error fetching tasks:", err);
                setError("Failed to load supervision data.");
                setData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchTasks();
    }, [currentUser, selectedSubType, pagination, fetchTrigger, debouncedSearchTerm]);

    // 3. Grouping Logic (Memoized) - Group by supervisor name or participant
    const groupedData = useMemo(() => {
        return data.reduce((groups: Record<string, any[]>, task) => {
            // Group by supervisor name if available, otherwise by participant
            const groupName = task.supervisorName ||
                (task.participant
                    ? `${task.participant.firstName} ${task.participant.lastName}`
                    : "Unassigned / General");

            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push(task);
            return groups;
        }, {});
    }, [data]);

    console.log('Grouped Data', groupedData)

    // Calculate days late
    const calculateDaysLate = (endDate: string) => {
        if (!endDate) return 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = new Date(endDate);
        dueDate.setHours(0, 0, 0, 0);
        const diffTime = dueDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays < 0 ? Math.abs(diffDays) : 0;
    };

    // Badge Base Class (same as Hometabs)
    const badgeBaseClass =
        "text-xs  text-black p-[0.14275rem]  flex items-center justify-center rounded-[6px] whitespace-nowrap";

    // Get SubType Badge Class Name
    const getSubTypeBadgeClassName = (subTypeName: string | undefined) => {
        const baseClass = "subtype-badge";
        if (!subTypeName) return `${baseClass} subtype-badge-default`;
        
        const normalizedSubType = subTypeName.toUpperCase().trim();

        switch (normalizedSubType) {
            case "MONTHLY":
                return `${baseClass} subtype-badge-monthly`;
            case "QUARTERLY":
                return `${baseClass} subtype-badge-quarterly`;
            case "ANNUAL":
                return `${baseClass} subtype-badge-annual`;
            case "INITIAL":
                return `${baseClass} subtype-badge-initial`;
            case "INTERIM":
                return `${baseClass} subtype-badge-interim`;
            default:
                return `${baseClass} subtype-badge-default`;
        }
    };

    // Get Status Badge Class Name (same as Hometabs)
    const getStatusBadgeClassName = (statusName: string) => {
        const baseClass = "badge-status-custom1 ";
        const normalizedStatus = statusName.toUpperCase().trim();

        switch (normalizedStatus) {
            case "NOT STARTED":
                return `${baseClass} badge-status-custom-not-started`;
            case "IN REVIEW (SUP)":
                return `${baseClass} badge-status-custom-in-review-sup`;
            case "IN REVIEW (PM)":
            case "IN REVIEW":
                return `${baseClass} badge-status-custom-in-review`;
            case "COMPLETED":
                return `${baseClass} badge-status-custom-completed`;
            case "PENDING EXTENSION":
                return `${baseClass} badge-status-custom-pending-extension`;
            case "EXTENDED":
                return `${baseClass} badge-status-custom-extended`;
            case "READY_":
            case "READY":
                return `${baseClass} badge-status-custom-ready`;
            case "ISSUES FOUND":
                return `${baseClass} badge-status-custom-issues`;
            case "CLOSED":
                return `${baseClass} badge-status-custom-closed`;
            default:
                return `${baseClass} badge-status-custom-default`;
        }
    };

    // Toggle Group Collapse
    const toggleGroup = (groupName: string) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [groupName]: !prev[groupName]
        }));
    };

    // 4. Handle Edit Click - REMOVED LOCK CHECK
    const handleEditClick = (task: any) => {
        const commentText = task.comments && task.comments.length > 0 ? task.comments[0].comment : "";
        const participantName = task.participant
            ? `${task.participant.firstName} ${task.participant.lastName}`
            : "";

        // Find the index in the original data array
        const originalIndex = data.findIndex(d => d.id === task.id);

        if (originalIndex !== -1) {
            setEditingRecordIndex(originalIndex);
            setCurrentComment(commentText);
            setModalParticipantName(participantName);
            setIsModalOpen(true);
        }
    };

    const handleCancelClick = () => {
        setIsModalOpen(false);
        setEditingRecordIndex(null);
        setCurrentComment("");
        setModalParticipantName("");
        setIsSavingComment(false);
    };

    // 5. Handle Save Comment - REMOVED LOCK ERROR HANDLING
    const handleSaveClick = async () => {
        if (editingRecordIndex === null || !currentUser) return;

        const recordToUpdate = data[editingRecordIndex];

        setIsSavingComment(true);
        try {
            const authToken = await currentUser.getIdToken();

            await postSupervisionComment({
                idToken: authToken,
                taskId: recordToUpdate.TaskId,
                commentText: currentComment,
                instanceId: supervisionInstance.id,
            });

            toast.success("Comment saved successfully!");
            setFetchTrigger((prev) => prev + 1); // Refresh data
            handleCancelClick();
        } catch (e: any) {
            console.error(e);
            toast.error("Failed to save comment. Please try again.");
        } finally {
            setIsSavingComment(false);
        }
    };

    return (
        <>
            {loading && <Loader />}
            {/* --- BREADCRUMB (Outside Container) --- */}
            <div className="weekly-supervision-breadcrumb">
                <Link to="/dashboard" className="breadcrumb-link">
                    Dashboard
                </Link>
                <span className="breadcrumb-separator">{">"}</span>
                <span className="breadcrumb-active">Weekly Supervision</span>
            </div>

            <div className="weekly-supervision-container">
                {/* --- SEARCH BAR AND NOTIFICATION BANNER --- */}
                <div className="weekly-supervision-top-section">
                    {/* --- SEARCH BAR --- */}
                    <div className="weekly-supervision-search-container">
                        <div className="weekly-supervision-search">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search by participant..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                    </div>

                    {/* --- NOTIFICATION BANNER (Purple) --- */}
                    {meetingDetails && (
                        <div className="weekly-supervision-notification">
                            <strong>Meeting Scheduled:</strong> Your weekly supervision meeting is set for{' '}
                            {moment(meetingDetails.startTime).format('MMM D, YYYY, h:mm A')}.
                        </div>
                    )}
                </div>

                {/* --- CARD LIST --- */}
                <div className="weekly-supervision-list">
                    {Object.keys(groupedData).length === 0 ? (
                        <div className="weekly-supervision-empty">
                            {loading ? "Loading..." : "No tasks found."}
                        </div>
                    ) : (
                        Object.keys(groupedData).map((groupName) => {
                            const isCollapsed = collapsedGroups[groupName];
                            const taskCount = groupedData[groupName].length;

                            return (
                                <div key={groupName} className="weekly-supervision-group">
                                    {/* GROUP HEADER CARD */}
                                    <div
                                        className="weekly-supervision-group-header"
                                        onClick={() => toggleGroup(groupName)}
                                    >
                                        <span className="group-name">{groupName}</span>
                                        <div className="group-actions">
                                            <button className="task-count-btn">
                                                {taskCount} Tasks
                                            </button>
                                            <FontAwesomeIcon
                                                icon={isCollapsed ? faChevronRight : faChevronDown}
                                                className="chevron-icon"
                                            />
                                        </div>
                                    </div>

                                    {/* TASK CARDS */}
                                    {!isCollapsed && groupedData[groupName].map((record: any) => {
                                        const daysLate = calculateDaysLate(record.endDate);
                                        const taskId = record.friendlyId;
                                        const orgAbbr = currentOrganization?.abbreviation || '';
                                        const fullTaskId = `${orgAbbr}-${taskId}`;
                                        const participantName = record.participant
                                            ? `${record.participant.firstName} ${record.participant.lastName}`
                                            : '';
                                        const commentText = record.comments && record.comments.length > 0
                                            ? record.comments[0].comment
                                            : '';
                                        const subTypeValue = record.subType?.name || record.subType?.type || record.subType || 'Monthly';
                                        const taskStatusName = record.taskStatus?.name || record.status?.name || '';

                                        return (
                                            <div
                                                key={record.id}
                                                className="weekly-supervision-task-card"
                                                onClick={() => navigate(`/task/${fullTaskId}`)}
                                            >
                                                <div className="task-card-left">
                                                    <div className="task-id-name">
                                                        {fullTaskId} {participantName}
                                                    </div>
                                                    <div className="task-title">{record.title}</div>
                                                </div>

                                                <div className="task-card-middle">
                                                    {daysLate > 0 && (
                                                        <span className="late-indicator">Late: {daysLate} days</span>
                                                    )}
                                                </div>

                                                <div className="task-card-right">
                                                    {/* SubType Badge - Monthly is purple with white text */}
                                                    <button className={getSubTypeBadgeClassName(subTypeValue)}>
                                                        {subTypeValue}
                                                    </button>

                                                    {/* Task Status Badge - Same as Hometabs */}
                                                    {taskStatusName && (
                                                        <span
                                                            className={`${getStatusBadgeClassName(
                                                                taskStatusName
                                                            )} ps-task-status-new gap-1 custom-badge rounded-1`}
                                                        >
                                                            <Flag size={12} />{" "}
                                                            {taskStatusName === "READY_"
                                                                ? "READY"
                                                                : taskStatusName}
                                                        </span>
                                                    )}

                                                    {/* Comment Section */}
                                                    <div
                                                        className="comment-section"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditClick(record);
                                                        }}
                                                    >
                                                        <span className={`comment-text ${!commentText ? 'comment-placeholder' : ''}`}>
                                                            {commentText || "Add a comment...."}
                                                        </span>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <g clip-path="url(#clip0_3546_18737)">
                                                                <path d="M14.1147 4.54126C14.4671 4.18888 14.6652 3.71091 14.6652 3.2125C14.6653 2.71409 14.4674 2.23607 14.115 1.8836C13.7626 1.53112 13.2846 1.33307 12.7862 1.33301C12.2878 1.33295 11.8098 1.53088 11.4573 1.88326L2.55999 10.7826C2.4052 10.9369 2.29073 11.127 2.22665 11.3359L1.34599 14.2373C1.32876 14.2949 1.32746 14.3562 1.34222 14.4145C1.35699 14.4728 1.38727 14.5261 1.42985 14.5686C1.47244 14.6111 1.52573 14.6413 1.58409 14.656C1.64245 14.6707 1.70369 14.6693 1.76132 14.6519L4.66332 13.7719C4.8721 13.7084 5.0621 13.5947 5.21665 13.4406L14.1147 4.54126Z" stroke="#7E57C2" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                                            </g>
                                                            <defs>
                                                                <clipPath id="clip0_3546_18737">
                                                                    <rect width="16" height="16" fill="white" />
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* --- PAGINATION --- */}
                {typeof totalCount === 'number' && (
                    <Pagination
                        currentPage={pagination.pageNumber}
                        totalPages={Math.ceil(totalCount / pagination.pageSize)}
                        onPageChange={(pageNum) => setPagination(prev => ({ ...prev, pageNumber: pageNum }))}
                        pageSize={pagination.pageSize}
                        resetPageSize={(pn, ps) => setPagination({ pageNumber: pn, pageSize: ps })}
                    />
                )}

                {/* --- COMMENT MODAL --- */}
                <CommentModal
                    isOpenComment={isModalOpen}
                    onClose={handleCancelClick}
                    onSave={handleSaveClick}
                    comment={currentComment}
                    setComment={setCurrentComment}
                    participantName={modalParticipantName}
                    loading={isSavingComment}
                />
            </div>
        </>
    );
};

export default WeeklySupervisionList;